import ReportsToolbar from "components/toolbars/ReportsToolbar";
import FormButton from "design/components/buttons/FormButton";
import CollapseCard from "design/components/cards/CollapseCard";
import TextInput from "design/components/forms/TextInput";
import RowView from "design/layouts/RowView";
import StackView from "design/layouts/StackView";
import WhiteBgArea from "design/layouts/WhiteBgArea";
import Page from "design/pages/Page";
import { t } from "i18next";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  addReport,
  getReportsPendingFinal,
  getReportsPendingManager,
  setHeadDecision,
  setManagerDecision,
  getAllReports,
  seedDummyDataIfNeeded,
} from "./store";
import type { ReportWorkflowItem } from "./types";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import { TextField, Typography } from "@material-ui/core";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import CheckCircleOutlinedIcon from "@material-ui/icons/CheckCircleOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

const PRIMARY = "rgba(14, 156, 240, 1)";
const PRIMARY_LIGHT = "rgba(14, 156, 240, 0.08)";
const PRIMARY_BORDER = "rgba(14, 156, 240, 0.35)";
const SUCCESS = "rgba(34, 167, 101, 1)";
const SUCCESS_BG = "rgba(34, 167, 101, 0.12)";
const SUCCESS_BORDER = "rgba(34, 167, 101, 0.35)";
const SUCCESS_TEXT = "rgba(34, 167, 101, 1)";
const REJECT = "rgba(226, 35, 45, 1)";
const REJECT_BG = "rgba(226, 35, 45, 0.1)";
const REJECT_BORDER = "rgba(226, 35, 45, 0.35)";
const REJECT_TEXT = "rgba(226, 35, 45, 1)";
const PENDING_BG = "rgba(126, 135, 145, 0.12)";
const PENDING_TEXT = "rgba(126, 135, 145, 1)";
const NEUTRAL_100 = "rgba(250, 251, 252, 1)";
const NEUTRAL_200 = "rgba(243, 244, 246, 1)";
const NEUTRAL_300 = "rgba(229, 231, 235, 1)";
const NEUTRAL_500 = "rgba(107, 114, 128, 1)";
const NEUTRAL_700 = "rgba(55, 65, 81, 1)";
const NEUTRAL_900 = "rgba(17, 24, 39, 1)";
const CARD_SHADOW = "0 2px 8px rgba(0,0,0,0.06)";
const CARD_SHADOW_HOVER = "0 4px 16px rgba(14, 156, 240, 0.12)";

const useStyles = makeThemedStyles("ReportWorkflow", (theme) => ({
  root: {
    padding: "32px 24px 48px",
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
  },
  viewModeTabsWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "32px",
  },
  viewModeTabs: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap" as const,
    paddingBottom: "20px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    justifyContent: "center",
  },
  centeredContent: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
  },
  submitFormCard: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    padding: "32px 28px 40px",
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: CARD_SHADOW,
    border: "1px solid rgba(0,0,0,0.06)",
  },
  submitFormTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "rgba(63, 69, 79, 0.95)",
    marginBottom: "24px",
    textAlign: "center" as const,
  },
  submitBtnWrap: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
  },
  submitBtnEnabled: {
    backgroundColor: PRIMARY + " !important",
    "&:hover": {
      backgroundColor: "rgba(14, 156, 240, 0.85) !important",
    },
  },
  formFieldWrap: {
    marginBottom: "20px",
  },
  formFieldLabel: {
    display: "block",
    fontSize: "12px",
    fontWeight: 700,
    color: "rgba(63, 69, 79, 0.8)",
    letterSpacing: "0.04em",
    marginBottom: "10px",
    textTransform: "uppercase" as const,
  },
  sampleDataBtn: {
    padding: "8px 16px",
    borderRadius: "12px",
    border: "1px dashed " + PRIMARY_BORDER,
    background: "transparent",
    color: PRIMARY,
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: PRIMARY_LIGHT,
      borderColor: PRIMARY,
    },
  },
  acceptedTypesHint: {
    fontSize: "12px",
    color: "rgba(63, 69, 79, 0.5)",
    marginTop: "8px",
  },
  viewModeBtn: {
    padding: "10px 20px",
    borderRadius: "24px",
    border: "1px solid transparent",
    background: "rgba(0,0,0,0.04)",
    color: "rgba(63, 69, 79, 0.85)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: "rgba(0,0,0,0.07)",
      color: "rgba(63, 69, 79, 1)",
    },
    "&.active": {
      background: PRIMARY,
      borderColor: PRIMARY,
      color: "#fff",
      fontWeight: 600,
      boxShadow: "0 2px 8px rgba(14, 156, 240, 0.35)",
    },
  },
  countBadge: {
    background: "rgba(255,255,255,0.25)",
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "12px",
    fontWeight: 600,
    "button.active &": {
      background: "rgba(255,255,255,0.35)",
    },
  },
  dropZone: {
    border: "2px dashed " + PRIMARY_BORDER,
    borderRadius: "16px",
    marginBottom: "10px",
    padding: "40px 32px",
    textAlign: "center" as const,
    backgroundColor: PRIMARY_LIGHT,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(14, 156, 240, 0.12)",
      borderColor: PRIMARY,
    },
    "&.dragging": {
      backgroundColor: "rgba(14, 156, 240, 0.18)",
      borderColor: PRIMARY,
      borderStyle: "solid",
    },
  },
  dropZoneIcon: {
    fontSize: "48px",
    color: PRIMARY,
    marginBottom: "12px",
    opacity: 0.9,
  },
  dropZoneText: {
    fontSize: "16px",
    fontWeight: 500,
    color: "rgba(63, 69, 79, 0.9)",
  },
  dropZoneHint: {
    fontSize: "13px",
    marginTop: "6px",
    color: "rgba(63, 69, 79, 0.55)",
  },
  dropZoneFile: {
    fontSize: "15px",
    fontWeight: 600,
    color: PRIMARY,
    wordBreak: "break-all" as const,
  },
  sectionCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "20px",
    boxShadow: CARD_SHADOW,
    border: "1px solid " + NEUTRAL_300,
  },
  sectionCardMuted: {
    backgroundColor: NEUTRAL_100,
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "20px",
    border: "1px solid " + NEUTRAL_300,
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: 700,
    marginBottom: "12px",
    color: NEUTRAL_700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  /* Commenting system */
  commentCard: {
    borderRadius: "16px",
    padding: "20px 22px",
    marginBottom: "20px",
    border: "1px solid " + NEUTRAL_300,
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  commentCardApproved: {
    borderLeft: "4px solid " + SUCCESS,
    backgroundColor: "rgba(34, 167, 101, 0.04)",
  },
  commentCardRejected: {
    borderLeft: "4px solid " + REJECT,
    backgroundColor: "rgba(226, 35, 45, 0.04)",
  },
  commentCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  commentCardIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
  },
  commentCardIconApproved: {
    backgroundColor: SUCCESS_BG,
    color: SUCCESS_TEXT,
  },
  commentCardIconRejected: {
    backgroundColor: REJECT_BG,
    color: REJECT_TEXT,
  },
  commentCardLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: NEUTRAL_700,
    letterSpacing: "0.02em",
  },
  commentCardMeta: {
    fontSize: "12px",
    color: NEUTRAL_500,
    marginTop: "4px",
  },
  commentCardBody: {
    fontSize: "15px",
    lineHeight: 1.55,
    color: NEUTRAL_900,
    whiteSpace: "pre-wrap" as const,
    margin: 0,
  },
  commentInputCard: {
    borderRadius: "16px",
    padding: "22px 24px",
    marginBottom: "20px",
    border: "2px solid " + PRIMARY_BORDER,
    backgroundColor: "rgba(14, 156, 240, 0.03)",
    boxShadow: "0 2px 8px rgba(14, 156, 240, 0.08)",
  },
  commentInputLabel: {
    display: "block",
    fontSize: "12px",
    fontWeight: 700,
    color: PRIMARY,
    letterSpacing: "0.04em",
    marginBottom: "10px",
    textTransform: "uppercase" as const,
  },
  commentInputField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: PRIMARY,
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: PRIMARY_BORDER,
      },
    },
  },
  actionRow: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap" as const,
    alignItems: "center",
    padding: "20px 0 8px",
    borderTop: "1px solid " + NEUTRAL_300,
    marginTop: "8px",
  },
  actionBtnApprove: {
    backgroundColor: SUCCESS + " !important",
    color: "#fff !important",
    border: "1px solid " + SUCCESS_BORDER + " !important",
    "&:hover": {
      backgroundColor: "rgba(34, 167, 101, 0.88) !important",
      borderColor: SUCCESS + " !important",
    },
    "&:disabled": {
      backgroundColor: NEUTRAL_200 + " !important",
      color: NEUTRAL_500 + " !important",
      borderColor: NEUTRAL_300 + " !important",
    },
  },
  actionBtnReject: {
    backgroundColor: REJECT + " !important",
    color: "#fff !important",
    border: "1px solid " + REJECT_BORDER + " !important",
    "&:hover": {
      backgroundColor: "rgba(226, 35, 45, 0.88) !important",
      borderColor: REJECT + " !important",
    },
    "&:disabled": {
      backgroundColor: NEUTRAL_200 + " !important",
      color: NEUTRAL_500 + " !important",
      borderColor: NEUTRAL_300 + " !important",
    },
  },
  formInputBase: {
    "& input": {
      borderRadius: "12px",
      border: "1px solid " + NEUTRAL_300,
      backgroundColor: "#fff",
      padding: "12px 14px",
      fontSize: "15px",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: NEUTRAL_500,
      },
      "&:focus": {
        outline: "none",
        borderColor: PRIMARY,
        boxShadow: "0 0 0 3px " + PRIMARY_LIGHT,
      },
    },
  },
  previewFrame: {
    width: "100%",
    minHeight: "520px",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.04)",
  },
  previewHeader: {
    padding: "12px 16px",
    background: "rgba(0,0,0,0.03)",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    fontSize: "13px",
    fontWeight: 600,
    color: "rgba(63, 69, 79, 0.8)",
  },
  reportCard: {
    padding: "18px 20px",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "12px",
    marginBottom: "12px",
    cursor: "pointer",
    backgroundColor: "#fff",
    boxShadow: CARD_SHADOW,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: PRIMARY_BORDER,
      boxShadow: CARD_SHADOW_HOVER,
      backgroundColor: PRIMARY_LIGHT,
    },
  },
  reportCardTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "rgba(63, 69, 79, 1)",
    marginBottom: "6px",
  },
  reportCardDesc: {
    fontSize: "14px",
    color: "rgba(63, 69, 79, 0.65)",
    lineHeight: 1.4,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as any,
    overflow: "hidden",
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    marginTop: "8px",
    alignSelf: "flex-start",
    width: "auto",
  },
  statusChipPending: {
    background: PENDING_BG,
    color: PENDING_TEXT,
  },
  statusChipApproved: {
    background: SUCCESS_BG,
    color: SUCCESS_TEXT,
  },
  statusChipRejected: {
    background: REJECT_BG,
    color: REJECT_TEXT,
  },
  commentBox: {
    marginTop: "16px",
    marginBottom: "8px",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 18px",
    borderRadius: "24px",
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    color: "rgba(63, 69, 79, 0.9)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: PRIMARY_BORDER,
      color: PRIMARY,
      backgroundColor: PRIMARY_LIGHT,
    },
  },
  detailTitle: {
    fontSize: "22px",
    fontWeight: 700,
    color: "rgba(63, 69, 79, 1)",
    margin: 0,
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "48px 24px",
    color: "rgba(63, 69, 79, 0.5)",
    fontSize: "15px",
  },
  emptyStateIcon: {
    fontSize: "56px",
    color: "rgba(0,0,0,0.12)",
    marginBottom: "16px",
  },
}));

type ViewMode = "submit" | "manager" | "head" | "mysubmissions";

const DEFAULT_REPORT_NAME = "Monthly Operations Report — March 2025";
const DEFAULT_DESCRIPTION =
  "Summary of key operations metrics, incident resolution rates, and capacity utilization. Includes recommendations for next month.";

const ReportWorkflow: FunctionComponent = () => {
  const classes = useStyles();
  const [viewMode, setViewMode] = useState<ViewMode>("submit");
  const [reportName, setReportName] = useState(DEFAULT_REPORT_NAME);
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);
  const [file, setFile] = useState<{ file: File; dataUrl: string } | null>(
    null,
  );
  const [dragActive, setDragActive] = useState(false);
  const [selectedReport, setSelectedReport] =
    useState<ReportWorkflowItem | null>(null);
  const [managerComment, setManagerComment] = useState("");
  const [headComment, setHeadComment] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const blob = new Blob(
      [
        "Sample report content for preview.\n\nThis is dummy data for demonstration.",
      ],
      {
        type: "text/plain",
      },
    );
    const url = URL.createObjectURL(blob);
    seedDummyDataIfNeeded(url);
  }, []);

  const loadSampleData = useCallback(() => {
    setReportName(DEFAULT_REPORT_NAME);
    setDescription(DEFAULT_DESCRIPTION);
    const blob = new Blob(
      ["Sample report content for preview.\n\nThis file is for demonstration."],
      {
        type: "text/plain",
      },
    );
    const url = URL.createObjectURL(blob);
    setFile({
      file: new File([blob], "Sample-Report-March-2025.txt", {
        type: "text/plain",
      }),
      dataUrl: url,
    });
  }, []);

  const pendingManager = getReportsPendingManager();
  const pendingFinal = getReportsPendingFinal();
  const allReports = getAllReports();

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) {
      const url = URL.createObjectURL(f);
      setFile({ file: f, dataUrl: url });
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) {
        setFile({ file: f, dataUrl: URL.createObjectURL(f) });
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (!reportName.trim() || !file) return;
    addReport({
      reportName: reportName.trim(),
      description: description.trim(),
      fileName: file.file.name,
      fileDataUrl: file.dataUrl,
    });
    setReportName("");
    setDescription("");
    setFile(null);
    refresh();
  }, [reportName, description, file, refresh]);

  const handleManagerApprove = useCallback(() => {
    if (!selectedReport || !managerComment.trim()) return;
    setManagerDecision(selectedReport.id, true, managerComment.trim());
    setSelectedReport(null);
    setManagerComment("");
    refresh();
  }, [selectedReport, managerComment, refresh]);

  const handleManagerReject = useCallback(() => {
    if (!selectedReport || !managerComment.trim()) return;
    setManagerDecision(selectedReport.id, false, managerComment.trim());
    setSelectedReport(null);
    setManagerComment("");
    refresh();
  }, [selectedReport, managerComment, refresh]);

  const handleHeadApprove = useCallback(() => {
    if (!selectedReport || !headComment.trim()) return;
    setHeadDecision(selectedReport.id, true, headComment.trim());
    setSelectedReport(null);
    setHeadComment("");
    refresh();
  }, [selectedReport, headComment, refresh]);

  const handleHeadReject = useCallback(() => {
    if (!selectedReport || !headComment.trim()) return;
    setHeadDecision(selectedReport.id, false, headComment.trim());
    setSelectedReport(null);
    setHeadComment("");
    refresh();
  }, [selectedReport, headComment, refresh]);

  const statusLabel = (status: ReportWorkflowItem["status"]) => {
    switch (status) {
      case "pending_manager":
        return t("reports.Pending manager approval");
      case "pending_final":
        return t("reports.Pending final approval");
      case "rejected_by_manager":
      case "rejected_final":
        return t("reports.Rejected");
      case "approved":
        return t("reports.Finally approved");
      default:
        return status;
    }
  };

  const statusChipClass = (status: ReportWorkflowItem["status"]) => {
    if (status === "approved") return classes.statusChipApproved;
    if (status === "rejected_by_manager" || status === "rejected_final")
      return classes.statusChipRejected;
    return classes.statusChipPending;
  };

  return (
    <Page Toolbar={ReportsToolbar}>
      <StackView gap="0px">
        <RowView>
          <WhiteBgArea width="100%">
            <div className={classes.root}>
              <div className={classes.viewModeTabsWrap}>
                <div className={classes.viewModeTabs}>
                  <button
                    type="button"
                    className={`${classes.viewModeBtn} ${
                      viewMode === "submit" ? "active" : ""
                    }`}
                    onClick={() => setViewMode("submit")}
                  >
                    <CloudUploadOutlinedIcon style={{ fontSize: 20 }} />
                    {t("reports.Submit for approval")}
                  </button>
                  <button
                    type="button"
                    className={`${classes.viewModeBtn} ${
                      viewMode === "mysubmissions" ? "active" : ""
                    }`}
                    onClick={() => setViewMode("mysubmissions")}
                  >
                    <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
                    {t("reports.My submissions")}
                    {allReports.length > 0 && (
                      <span className={classes.countBadge}>
                        {allReports.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${classes.viewModeBtn} ${
                      viewMode === "manager" ? "active" : ""
                    }`}
                    onClick={() => setViewMode("manager")}
                  >
                    <ScheduleIcon style={{ fontSize: 20 }} />
                    {t("reports.Manager approval")}
                    {pendingManager.length > 0 && (
                      <span className={classes.countBadge}>
                        {pendingManager.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${classes.viewModeBtn} ${
                      viewMode === "head" ? "active" : ""
                    }`}
                    onClick={() => setViewMode("head")}
                  >
                    <CheckCircleOutlinedIcon style={{ fontSize: 20 }} />
                    {t("reports.Final approval")}
                    {pendingFinal.length > 0 && (
                      <span className={classes.countBadge}>
                        {pendingFinal.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
              {/* Stage 1: Submit new report */}
              {viewMode === "submit" && (
                <div className={classes.centeredContent}>
                  <div className={classes.submitFormCard}>
                    <div className={classes.submitFormTitle}>
                      {t("reports.Submit for approval")}
                    </div>
                    <div
                      className={`${classes.dropZone} ${
                        dragActive ? "dragging" : ""
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() =>
                        document
                          .getElementById("report-workflow-file-input")
                          ?.click()
                      }
                    >
                      <input
                        id="report-workflow-file-input"
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                      />
                      {file ? (
                        <div className={classes.dropZoneFile}>
                          <DescriptionOutlinedIcon
                            style={{
                              fontSize: 28,
                              verticalAlign: "middle",
                              marginRight: 8,
                            }}
                          />
                          {file.file.name}
                        </div>
                      ) : (
                        <>
                          <CloudUploadOutlinedIcon
                            className={classes.dropZoneIcon}
                          />
                          <div className={classes.dropZoneText}>
                            {t("reports.Drag and drop your report file here")}
                          </div>
                          <div className={classes.dropZoneHint}>
                            {t("reports.or click to browse")}
                          </div>
                        </>
                      )}
                    </div>
                    <div className={classes.formFieldWrap}>
                      <label className={classes.formFieldLabel}>
                        {t("reports.Report name")}
                      </label>
                      <div className={classes.formInputBase}>
                        <TextInput
                          fullWidth
                          value={reportName}
                          onChange={setReportName}
                        />
                      </div>
                    </div>
                    <div className={classes.formFieldWrap}>
                      <label className={classes.formFieldLabel}>
                        {t("reports.Report description")}
                      </label>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t("reports.Report description")}
                        style={{
                          borderRadius: "12px",
                        }}
                        InputProps={{
                          style: {
                            borderRadius: "12px",
                            backgroundColor: "#fff",
                            border: "1px solid " + NEUTRAL_300,
                          },
                        }}
                        className={classes.commentInputField}
                      />
                    </div>
                    <div className={classes.acceptedTypesHint}>
                      PDF, Word, Excel (.pdf, .doc, .docx, .xls, .xlsx)
                    </div>
                    <div className={classes.submitBtnWrap}>
                      <FormButton
                        type="button"
                        style="submit"
                        onClick={handleSubmit}
                        disabled={!reportName.trim() || !file}
                        className={
                          reportName.trim() && file
                            ? classes.submitBtnEnabled
                            : undefined
                        }
                      >
                        {t("reports.Submit for approval")}
                      </FormButton>
                    </div>
                  </div>
                </div>
              )}

              {/* My submissions list */}
              {viewMode === "mysubmissions" && (
                <div className={classes.centeredContent}>
                  <div
                    className={classes.sectionTitle}
                    style={{ marginBottom: 16 }}
                  >
                    {t("reports.My submissions")}
                  </div>
                  {allReports.length === 0 ? (
                    <div className={classes.emptyState}>
                      <DescriptionOutlinedIcon
                        className={classes.emptyStateIcon}
                      />
                      <Typography>
                        {t("reports.No reports submitted yet")}
                      </Typography>
                    </div>
                  ) : !selectedReport ? (
                    allReports.map((r) => (
                      <div
                        key={r.id}
                        className={classes.reportCard}
                        onClick={() => setSelectedReport(r)}
                      >
                        <div className={classes.reportCardTitle}>
                          {r.reportName}
                        </div>
                        {r.description ? (
                          <div className={classes.reportCardDesc}>
                            {r.description}
                          </div>
                        ) : null}
                        <span
                          className={`${classes.statusChip} ${statusChipClass(
                            r.status,
                          )}`}
                        >
                          {statusLabel(r.status)}
                        </span>
                      </div>
                    ))
                  ) : null}
                </div>
              )}

              {/* My submissions: detail view with preview */}
              {viewMode === "mysubmissions" && selectedReport && (
                <div className={classes.centeredContent}>
                  <StackView gap="24px">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "12px",
                      }}
                    >
                      <Typography className={classes.detailTitle}>
                        {selectedReport.reportName}
                      </Typography>
                      <button
                        type="button"
                        className={classes.backBtn}
                        onClick={() => setSelectedReport(null)}
                      >
                        <ArrowBackIcon style={{ fontSize: 20 }} />
                        {t("reports.Back")}
                      </button>
                    </div>
                    <span
                      className={`${classes.statusChip} ${statusChipClass(
                        selectedReport.status,
                      )}`}
                      style={{ marginTop: 0, alignSelf: "flex-start" }}
                    >
                      {statusLabel(selectedReport.status)}
                    </span>
                    <div className={classes.sectionCardMuted}>
                      <div className={classes.sectionTitle}>
                        {t("reports.Report description")}
                      </div>
                      <Typography className={classes.commentCardBody}>
                        {selectedReport.description || "—"}
                      </Typography>
                    </div>
                    {selectedReport.managerComment && (
                      <div
                        className={`${classes.commentCard} ${
                          selectedReport.managerComment.approved
                            ? classes.commentCardApproved
                            : classes.commentCardRejected
                        }`}
                      >
                        <div className={classes.commentCardHeader}>
                          <div
                            className={`${classes.commentCardIcon} ${
                              selectedReport.managerComment.approved
                                ? classes.commentCardIconApproved
                                : classes.commentCardIconRejected
                            }`}
                          >
                            {selectedReport.managerComment.approved ? (
                              <CheckCircleIcon style={{ fontSize: 20 }} />
                            ) : (
                              <CancelIcon style={{ fontSize: 20 }} />
                            )}
                          </div>
                          <div>
                            <div className={classes.commentCardLabel}>
                              {t("reports.Previous approval comment")}
                            </div>
                            <div className={classes.commentCardMeta}>
                              {selectedReport.managerComment.approved
                                ? t("reports.Approved by manager")
                                : t("reports.Rejected")}
                              {" · "}
                              {new Date(
                                selectedReport.managerComment.at,
                              ).toLocaleDateString(undefined, {
                                dateStyle: "medium",
                              })}
                            </div>
                          </div>
                        </div>
                        <p className={classes.commentCardBody}>
                          {selectedReport.managerComment.comment}
                        </p>
                      </div>
                    )}
                    {selectedReport.headComment && (
                      <div
                        className={`${classes.commentCard} ${
                          selectedReport.headComment.approved
                            ? classes.commentCardApproved
                            : classes.commentCardRejected
                        }`}
                      >
                        <div className={classes.commentCardHeader}>
                          <div
                            className={`${classes.commentCardIcon} ${
                              selectedReport.headComment.approved
                                ? classes.commentCardIconApproved
                                : classes.commentCardIconRejected
                            }`}
                          >
                            {selectedReport.headComment.approved ? (
                              <CheckCircleIcon style={{ fontSize: 20 }} />
                            ) : (
                              <CancelIcon style={{ fontSize: 20 }} />
                            )}
                          </div>
                          <div>
                            <div className={classes.commentCardLabel}>
                              {t("reports.Final approval")}
                            </div>
                            <div className={classes.commentCardMeta}>
                              {selectedReport.headComment.approved
                                ? t("reports.Finally approved")
                                : t("reports.Finally rejected")}
                              {" · "}
                              {new Date(
                                selectedReport.headComment.at,
                              ).toLocaleDateString(undefined, {
                                dateStyle: "medium",
                              })}
                            </div>
                          </div>
                        </div>
                        <p className={classes.commentCardBody}>
                          {selectedReport.headComment.comment}
                        </p>
                      </div>
                    )}
                    <div
                      className={classes.sectionCard}
                      style={{ padding: 0, overflow: "hidden" }}
                    >
                      <div className={classes.previewHeader}>
                        {t("reports.Preview report")} —{" "}
                        {selectedReport.fileName}
                      </div>
                      {selectedReport.fileName
                        .toLowerCase()
                        .endsWith(".pdf") ? (
                        <iframe
                          title="Report preview"
                          src={selectedReport.fileDataUrl}
                          className={classes.previewFrame}
                        />
                      ) : (
                        <div
                          className={classes.previewFrame}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(0,0,0,0.03)",
                            minHeight: "280px",
                          }}
                        >
                          <div style={{ textAlign: "center", padding: 24 }}>
                            <DescriptionOutlinedIcon
                              style={{
                                fontSize: 48,
                                color: "rgba(0,0,0,0.2)",
                                marginBottom: 12,
                              }}
                            />
                            <Typography style={{ marginBottom: 12 }}>
                              {selectedReport.fileName}
                            </Typography>
                            <a
                              href={selectedReport.fileDataUrl}
                              download={selectedReport.fileName}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: PRIMARY,
                                fontWeight: 600,
                                textDecoration: "none",
                              }}
                            >
                              Download file
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </StackView>
                </div>
              )}

              {/* Stage 2: Manager approval queue */}
              {viewMode === "manager" && !selectedReport && (
                <div className={classes.centeredContent}>
                  <div
                    className={classes.sectionTitle}
                    style={{ marginBottom: 16 }}
                  >
                    {t("reports.Pending manager approval")}
                  </div>
                  {pendingManager.length === 0 ? (
                    <div className={classes.emptyState}>
                      <ScheduleIcon className={classes.emptyStateIcon} />
                      <Typography>
                        {t("reports.No reports pending.")}
                      </Typography>
                    </div>
                  ) : (
                    pendingManager.map((r) => (
                      <div
                        key={r.id}
                        className={classes.reportCard}
                        onClick={() => setSelectedReport(r)}
                      >
                        <div className={classes.reportCardTitle}>
                          {r.reportName}
                        </div>
                        <div className={classes.reportCardDesc}>
                          {r.description || "—"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Stage 3: Head manager approval queue */}
              {viewMode === "head" && !selectedReport && (
                <div className={classes.centeredContent}>
                  <div
                    className={classes.sectionTitle}
                    style={{ marginBottom: 16 }}
                  >
                    {t("reports.Pending final approval")}
                  </div>
                  {pendingFinal.length === 0 ? (
                    <div className={classes.emptyState}>
                      <CheckCircleOutlinedIcon
                        className={classes.emptyStateIcon}
                      />
                      <Typography>
                        {t("reports.No reports pending.")}
                      </Typography>
                    </div>
                  ) : (
                    pendingFinal.map((r) => (
                      <div
                        key={r.id}
                        className={classes.reportCard}
                        onClick={() => setSelectedReport(r)}
                      >
                        <div className={classes.reportCardTitle}>
                          {r.reportName}
                        </div>
                        <div className={classes.reportCardDesc}>
                          {r.description || "—"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Detail view: preview + approve/reject (manager or head) */}
              {selectedReport &&
                (viewMode === "manager" || viewMode === "head") && (
                  <div className={classes.centeredContent}>
                    <StackView gap="24px">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <Typography className={classes.detailTitle}>
                          {selectedReport.reportName}
                        </Typography>
                        <button
                          type="button"
                          className={classes.backBtn}
                          onClick={() => {
                            setSelectedReport(null);
                            setManagerComment("");
                            setHeadComment("");
                          }}
                        >
                          <ArrowBackIcon style={{ fontSize: 20 }} />
                          {t("reports.Back")}
                        </button>
                      </div>
                      <div className={classes.sectionCardMuted}>
                        <div className={classes.sectionTitle}>
                          {t("reports.Report description")}
                        </div>
                        <Typography className={classes.commentCardBody}>
                          {selectedReport.description || "—"}
                        </Typography>
                      </div>
                      {viewMode === "head" && selectedReport.managerComment && (
                        <div
                          className={`${classes.commentCard} ${
                            selectedReport.managerComment.approved
                              ? classes.commentCardApproved
                              : classes.commentCardRejected
                          }`}
                        >
                          <div className={classes.commentCardHeader}>
                            <div
                              className={`${classes.commentCardIcon} ${
                                selectedReport.managerComment.approved
                                  ? classes.commentCardIconApproved
                                  : classes.commentCardIconRejected
                              }`}
                            >
                              {selectedReport.managerComment.approved ? (
                                <CheckCircleIcon style={{ fontSize: 20 }} />
                              ) : (
                                <CancelIcon style={{ fontSize: 20 }} />
                              )}
                            </div>
                            <div>
                              <div className={classes.commentCardLabel}>
                                {t("reports.Previous approval comment")}
                              </div>
                              <div className={classes.commentCardMeta}>
                                {selectedReport.managerComment.approved
                                  ? t("reports.Approved by manager")
                                  : t("reports.Rejected")}
                                {" · "}
                                {new Date(
                                  selectedReport.managerComment.at,
                                ).toLocaleDateString(undefined, {
                                  dateStyle: "medium",
                                })}
                              </div>
                            </div>
                          </div>
                          <p className={classes.commentCardBody}>
                            {selectedReport.managerComment.comment}
                          </p>
                        </div>
                      )}
                      <div
                        className={classes.sectionCard}
                        style={{ padding: 0, overflow: "hidden" }}
                      >
                        <div className={classes.previewHeader}>
                          {t("reports.Preview report")} —{" "}
                          {selectedReport.fileName}
                        </div>
                        {selectedReport.fileName
                          .toLowerCase()
                          .endsWith(".pdf") ? (
                          <iframe
                            title="Report preview"
                            src={selectedReport.fileDataUrl}
                            className={classes.previewFrame}
                          />
                        ) : (
                          <div
                            className={classes.previewFrame}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(0,0,0,0.03)",
                              minHeight: "280px",
                            }}
                          >
                            <div style={{ textAlign: "center", padding: 24 }}>
                              <DescriptionOutlinedIcon
                                style={{
                                  fontSize: 48,
                                  color: "rgba(0,0,0,0.2)",
                                  marginBottom: 12,
                                }}
                              />
                              <Typography style={{ marginBottom: 12 }}>
                                {selectedReport.fileName}
                              </Typography>
                              <a
                                href={selectedReport.fileDataUrl}
                                download={selectedReport.fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: PRIMARY,
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                Download file
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className={classes.commentInputCard}>
                        <label className={classes.commentInputLabel}>
                          {viewMode === "manager"
                            ? t("reports.Manager approval")
                            : t("reports.Final approval")}
                          — {t("reports.Comment")}
                        </label>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          variant="outlined"
                          value={
                            viewMode === "manager"
                              ? managerComment
                              : headComment
                          }
                          onChange={(e) =>
                            viewMode === "manager"
                              ? setManagerComment(e.target.value)
                              : setHeadComment(e.target.value)
                          }
                          placeholder={
                            viewMode === "manager"
                              ? "Add your review comment (required for approve or reject)..."
                              : "Add your final decision comment (required)..."
                          }
                          className={classes.commentInputField}
                          InputProps={{
                            style: {
                              borderRadius: "12px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </div>
                      <div className={classes.actionRow}>
                        <FormButton
                          type="button"
                          style="approve"
                          onClick={
                            viewMode === "manager"
                              ? handleManagerApprove
                              : handleHeadApprove
                          }
                          disabled={
                            viewMode === "manager"
                              ? !managerComment.trim()
                              : !headComment.trim()
                          }
                          className={classes.actionBtnApprove}
                        >
                          {t("reports.Approve")}
                        </FormButton>
                        <FormButton
                          type="button"
                          style="reject"
                          onClick={
                            viewMode === "manager"
                              ? handleManagerReject
                              : handleHeadReject
                          }
                          disabled={
                            viewMode === "manager"
                              ? !managerComment.trim()
                              : !headComment.trim()
                          }
                          className={classes.actionBtnReject}
                        >
                          {t("reports.Reject")}
                        </FormButton>
                      </div>
                    </StackView>
                  </div>
                )}
            </div>
          </WhiteBgArea>
        </RowView>
      </StackView>
    </Page>
  );
};

export default ReportWorkflow;
